import React, {useCallback, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';
import NoDataLabel from '../../app/components/layout/NoDataLabel';
import PhotoLightbox from '../../app/components/layout/PhotoLightbox';
import Constants from '../../utils/constants';
import {synthesesActions} from '../../modules/syntheses/syntheses.actions';
import {useGetTerritoireQuery} from '../api.js';
import {indexer} from '../selectors/index.js';
import {useCollecte} from '../collecte.js';
import {etatParDefaut, toQuery} from '../permalink.js';
import {VERDICT_COULEURS} from '../tokens.js';
import Coquille from '../components/Coquille.js';
import FicheBandeau from '../components/fiche/FicheBandeau.js';
import FicheBlocsPassage from '../components/fiche/FicheBlocsPassage.js';
import FicheEtatVide from '../components/fiche/FicheEtatVide.js';
import FicheFilAriane from '../components/fiche/FicheFilAriane.js';
import FicheHistorique from '../components/fiche/FicheHistorique.js';
import FicheRail from '../components/fiche/FicheRail.js';
import FicheSquelette from '../components/fiche/FicheSquelette.js';
import {
    blocsDuPassage, dernierPassage, lignesHistorique,
    prochainPassage, serieCollectesPav, serieLavagesPav, statsCollecte, statsLavage, verdictCourant,
} from '../components/fiche/fiche.selectors.js';

const aujourdhui = () => new Date().toLocaleDateString('sv-SE'); // Y-m-d, fuseau local

/** Écran 3 : Fiche PAV (PIL-01 lot 3). États : chargement, PAV introuvable, sans passage ERP, chargé. */
export default function FichePav() {
    const {slug, pavId} = useParams();
    const dispatch = useDispatch();
    const {currentData: data, isLoading: chargement, isFetching, isError} = useGetTerritoireQuery(slug);
    const isLoading = chargement || (isFetching && !data);
    const [lightbox, setLightbox] = useState(null); // {photos: [{src, label, color}], index}

    const [collecte] = useCollecte();
    const index = useMemo(() => (data ? indexer(data, {collecte}) : null), [data, collecte]);
    const pav = index ? index.pavById.get(pavId) : null;

    const vue = useMemo(() => {
        if (!index || !pav) return null;
        const liste = index.evenementsParPav.get(pav.id) || [];
        const passage = dernierPassage(liste);
        const jour = aujourdhui();
        const base = etatParDefaut(index, jour);
        const courant = verdictCourant(liste, jour, index.typologiesMaintenanceParPatrimoine);
        const lien = (surcharge) => `/pilotage/${slug}/carte?${toQuery({...base, ...surcharge}, index)}`;
        const centre = pav.lat != null && pav.lng != null ? {c: {lat: pav.lat, lng: pav.lng}, z: 16} : {};
        const matrice = index.snapshot.rse && index.snapshot.rse.eau ? index.snapshot.rse.eau.matrice : null;
        return {
            passage,
            verdict: courant.verdict,
            avecLavage: courant.avecLavage,
            blocs: blocsDuPassage(passage),
            elementDefaut: courant.elementDefaut,
            prochain: prochainPassage(liste),
            lignes: lignesHistorique(liste, pav.quantite),
            lavage: statsLavage(liste, matrice),
            collecte: index.collecte ? statsCollecte(liste) : null,
            courbeLavages: serieLavagesPav(liste, matrice),
            courbeCollectes: index.collecte ? serieCollectesPav(liste) : [],
            liens: {
                carte: lien({}),
                carteCentree: lien(centre),
                commune: pav.communeCle ? lien({commune: pav.communeCle}) : null,
            },
        };
    }, [index, pav, slug]);

    // R6 : même appel que `InterventionsPage.onPDFClick` (la date n'est pas utilisée par l'action).
    const onPdf = useCallback((interventionId) => {
        dispatch(synthesesActions.fetchInterventionPDF(interventionId));
    }, [dispatch]);

    const ouvrirPhotos = useCallback((photos, src) => {
        setLightbox({photos, index: Math.max(0, photos.findIndex((p) => p.src === src))});
    }, []);

    let corps;
    if (isLoading) {
        corps = <FicheSquelette/>;
    } else if (isError || !index) {
        corps = <NoDataLabel label="Territoire indisponible"/>;
    } else if (!pav) {
        corps = <NoDataLabel label="PAV introuvable"/>;
    } else {
        const patrimoine = index.patrimoineById.get(pav.patrimoineId) || null;
        const {passage} = vue;
        corps = (
            <>
                <FicheFilAriane carteTo={vue.liens.carte} communeLibelle={pav.communeLibelle} communeTo={vue.liens.commune} nomenclature={pav.nomenclature}/>
                {!passage ? (
                    <FicheEtatVide pav={pav} patrimoine={patrimoine} prochain={vue.prochain} carteTo={vue.liens.carteCentree} voisinsTo={vue.liens.carteCentree}/>
                ) : (
                    <>
                        <FicheBandeau pav={pav} verdict={vue.verdict} avecLavage={vue.avecLavage} passage={passage}
                                      elementDefaut={vue.elementDefaut} prochain={vue.prochain} onPdf={onPdf}/>
                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 340px'}, gap: '24px', alignItems: 'start'}}>
                            <FicheBlocsPassage blocs={vue.blocs} quantite={pav.quantite} onPhotos={ouvrirPhotos}/>
                            <FicheRail pav={pav} patrimoine={patrimoine} carteTo={vue.liens.carteCentree} lavage={vue.lavage}
                                       collecte={vue.collecte} courbeLavages={vue.courbeLavages} courbeCollectes={vue.courbeCollectes} couleurMarqueur={(VERDICT_COULEURS[vue.verdict] || VERDICT_COULEURS.ni).pastille}/>
                        </Box>
                        <FicheHistorique lignes={vue.lignes} collecte={index.collecte} onPdf={onPdf}/>
                    </>
                )}
            </>
        );
    }

    return (
        <Coquille slug={slug} onglet="fiche" ongletFiche={pav ? pav.nomenclature : null} index={index}>
            <Box key={pavId} sx={{maxWidth: 1376, width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: '20px'}}>
                {corps}
            </Box>
            <PhotoLightbox
                photos={lightbox ? lightbox.photos : []}
                index={lightbox ? lightbox.index : null}
                onClose={() => setLightbox(null)}
                onIndexChange={(i) => setLightbox((l) => (l ? {...l, index: i} : l))}
                assetsUrl={Constants.API_ASSETS_PHOTO_INTERVENTION}
            />
        </Coquille>
    );
}
